import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import {
  ApiError,
  downloadInvoicePdf,
  useInvoice,
  useInvoices,
  type InvoiceDetailDto,
  type InvoiceListItemDto,
} from "@/hooks/useInvoices";
import type { InvoiceRelationship, StrategyPricingTier } from "@/lib/api";
import { formatDate, formatMoneyFromString } from "@/utils/format";

const TIER_LABELS: Record<StrategyPricingTier, string> = {
  retail: "Retail",
  wholesale: "Wholesale",
  reseller_retail: "Reseller Retail",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatPeriod(periodYear: number, periodMonth: number): string {
  const name = MONTH_NAMES[periodMonth - 1] ?? String(periodMonth);
  return `${name} ${periodYear}`;
}

/**
 * SFWL-015: super-admin/tenant billing surface. `WriteAccessGuard` on every
 * `v1/billing` route including GETs is the real access-control gate (view-
 * only members always 403 — see `useInvoices`' doc-comment); this page's
 * job is UX only: hide the nav entry (see `Header.tsx`) and, for a member
 * who lands here anyway (direct link, stale bookmark), toast + redirect
 * away rather than sitting on a page that can never load.
 */
export default function Billing() {
  const navigate = useNavigate();
  const { invoices, isLoading, error } = useInvoices();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;
    if (error instanceof ApiError && error.status === 403) {
      toast.error("You don't have access to billing.");
      navigate("/strategies");
      return;
    }
    toast.error(error.message);
  }, [error, navigate]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDownload = async (invoice: InvoiceListItemDto) => {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoicePdf(invoice.id, invoice.invoiceNumber);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDownloadingId(null);
    }
  };

  // A 403 redirects away above — nothing else to render for that case, but
  // guard against a render sneaking in before the effect fires.
  const is403 = error instanceof ApiError && error.status === 403;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-white">
      <title>Billing | Sovrun</title>
      <h1 className="mb-2 text-2xl font-bold text-white">Billing</h1>
      <p className="mb-6 text-sm text-slate-400">
        Monthly invoices for your account, plus any child accounts you administer.
      </p>

      {error && !is403 && (
        <div className="mb-6 rounded-lg border border-rose-500/50 bg-rose-500/10 p-4 text-rose-400">
          <p className="font-semibold">Couldn&apos;t load invoices</p>
          <p className="mt-1 text-sm">{error.message}</p>
        </div>
      )}

      {isLoading && !error && <p className="text-sm text-slate-400">Loading invoices…</p>}

      {!isLoading && !error && invoices.length === 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-8 text-center">
          <p className="text-sm text-slate-400 italic">No invoices yet.</p>
        </div>
      )}

      {!isLoading && !error && invoices.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-700">
          <table className="min-w-full divide-y divide-slate-700 text-sm">
            <thead className="bg-slate-800/50 text-left text-xs font-medium uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2" />
                <th className="px-4 py-2">Invoice #</th>
                <th className="px-4 py-2">Period</th>
                <th className="px-4 py-2">Account</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Total</th>
                <th className="px-4 py-2">Issued</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  expanded={expandedIds.has(invoice.id)}
                  onToggle={() => toggleExpanded(invoice.id)}
                  onDownload={() => handleDownload(invoice)}
                  isDownloading={downloadingId === invoice.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: InvoiceListItemDto["status"] }) {
  const isPaid = status === "paid";
  return (
    <span
      className={
        isPaid
          ? "rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-semibold text-emerald-400"
          : "rounded-full bg-sky-500/20 px-2 py-0.5 text-xs font-semibold text-sky-400"
      }
    >
      {isPaid ? "Paid" : "Issued"}
    </span>
  );
}

function RelationshipBadge({
  relationship,
  tenantName,
}: {
  relationship: InvoiceRelationship;
  tenantName: string;
}) {
  if (relationship === "own") {
    return <span className="text-slate-300">Own account</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="rounded-full bg-indigo-500/20 px-2 py-0.5 text-xs font-semibold text-indigo-300">
        Child
      </span>
      <span className="text-slate-300">{tenantName}</span>
    </span>
  );
}

function InvoiceRow({
  invoice,
  expanded,
  onToggle,
  onDownload,
  isDownloading,
}: {
  invoice: InvoiceListItemDto;
  expanded: boolean;
  onToggle: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}) {
  return (
    <>
      <tr>
        <td className="px-4 py-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? `Collapse invoice ${invoice.invoiceNumber}`
                : `Expand invoice ${invoice.invoiceNumber}`
            }
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            {expanded ? "−" : "+"}
          </button>
        </td>
        <td className="px-4 py-2 font-medium text-white">{invoice.invoiceNumber}</td>
        <td className="px-4 py-2">
          {formatPeriod(invoice.periodYear, invoice.periodMonth)}
        </td>
        <td className="px-4 py-2">
          <RelationshipBadge
            relationship={invoice.relationship}
            tenantName={invoice.tenantName}
          />
        </td>
        <td className="px-4 py-2">
          <span className="inline-flex items-center gap-1.5">
            <StatusBadge status={invoice.status} />
            {/* Review-fleet fix: tenant admins can see *that* an invoice
                email didn't go out, without the raw provider error text —
                emailError was deliberately removed from the tenant-facing
                DTO (see app/lib/api.ts's InvoiceListItemDto doc-comment). */}
            {invoice.emailedAt === null && (
              <span className="text-xs text-slate-500">Not emailed</span>
            )}
          </span>
        </td>
        <td className="px-4 py-2 font-semibold text-white">
          {formatMoneyFromString(invoice.total, invoice.currency)}
        </td>
        <td className="px-4 py-2 text-slate-400">{formatDate(invoice.issuedAt)}</td>
        <td className="px-4 py-2">
          <button
            type="button"
            onClick={onDownload}
            disabled={isDownloading}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-800 disabled:opacity-50"
          >
            {isDownloading ? "Downloading…" : "Download PDF"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="bg-slate-900/50 px-4 py-4">
            <InvoiceLineItemsPanel invoiceId={invoice.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoiceLineItemsPanel({ invoiceId }: { invoiceId: string }) {
  const { invoice, isLoading, error } = useInvoice(invoiceId);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  if (isLoading) {
    return <p className="text-sm text-slate-400">Loading line items…</p>;
  }

  if (error) {
    return (
      <p className="text-sm text-rose-400">
        Couldn&apos;t load line items: {error.message}
      </p>
    );
  }

  if (!invoice) return null;

  return <InvoiceDetail invoice={invoice} />;
}

function InvoiceDetail({ invoice }: { invoice: InvoiceDetailDto }) {
  return (
    <div>
      {invoice.relationship === "child" && (
        <p className="mb-3 text-xs text-slate-400">
          Billed to: {invoice.billTo.companyName ?? invoice.tenantId}
        </p>
      )}
      {invoice.lineItems.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No line items.</p>
      ) : (
        <table className="min-w-full divide-y divide-slate-800 text-sm">
          <thead className="text-left text-xs font-medium uppercase text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Strategy</th>
              <th className="px-3 py-1.5">Tier</th>
              <th className="px-3 py-1.5">Qty</th>
              <th className="px-3 py-1.5">Unit /mo</th>
              <th className="px-3 py-1.5">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {invoice.lineItems.map((line) => (
              <tr key={line.id}>
                <td className="px-3 py-1.5 text-white">{line.strategyName}</td>
                <td className="px-3 py-1.5 text-slate-400">{TIER_LABELS[line.tier]}</td>
                <td className="px-3 py-1.5 text-slate-300">{line.quantity}</td>
                <td className="px-3 py-1.5">
                  {line.promoCode ? (
                    <span className="flex items-center gap-1.5">
                      <span className="text-slate-500 line-through">
                        {formatMoneyFromString(line.unitPriceMonthly, invoice.currency)}
                      </span>
                      <span className="font-semibold text-emerald-400">
                        {formatMoneyFromString(line.effectiveUnitPrice, invoice.currency)}
                      </span>
                      <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-400">
                        {line.promoCode}
                      </span>
                    </span>
                  ) : (
                    <span className="text-slate-300">
                      {formatMoneyFromString(line.effectiveUnitPrice, invoice.currency)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 font-semibold text-white">
                  {formatMoneyFromString(line.amount, invoice.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
